import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  DeploymentTaskDto,
  EmbedReportsResponseDto,
  PaginatedResult,
  PbixFileInfo,
  PowerbiReportSummaryDto,
  ReportRefreshStatusDto,
} from 'types/DataResult';
import { Observable } from 'rxjs';
import { RESTServerRoute } from 'types/Server';

@Injectable()
export class EmbeddedPowerBiApiService {
  private readonly timeZone = this.safeGetTimezone();

  public constructor(private readonly httpClient: HttpClient) {}

  public getEmbedReports(): Observable<EmbedReportsResponseDto> {
    const headers = new HttpHeaders().set('time-zone', this.timeZone);
    return this.httpClient.get<EmbedReportsResponseDto>(RESTServerRoute.REST_EMBED_REPORTS, {
      headers,
    });
  }

  public getReportsInS3(): Observable<PbixFileInfo[]> {
    return this.httpClient.get<PbixFileInfo[]>(RESTServerRoute.REST_GET_AVAILABLE_REPORTS);
  }

  public getAllTenantReports(): Observable<Record<string, PowerbiReportSummaryDto[]>> {
    return this.httpClient.get<Record<string, PowerbiReportSummaryDto[]>>(RESTServerRoute.REST_GET_ALL_TENANT_REPORTS);
  }

  public publishTenantsReport(reportName: string, tenantIds: string[]): Observable<void> {
    return this.httpClient.put<void>(
      RESTServerRoute.REST_PUBLISH_REPORT.replace(':reportName', encodeURIComponent(reportName)),
      {
        tenantIds,
      },
    );
  }

  public getDeploymentTasks(page: number, limit: number): Observable<PaginatedResult<DeploymentTaskDto>> {
    return this.httpClient.get<PaginatedResult<DeploymentTaskDto>>(RESTServerRoute.REST_GET_DEPLOYMENT_TASKS, {
      params: { page: page.toString(), limit: limit.toString() },
    });
  }

  public getReportRefreshStatus(reportId: string): Observable<ReportRefreshStatusDto> {
    return this.httpClient.get<ReportRefreshStatusDto>(
      RESTServerRoute.REST_REPORT_REFRESH_STATUS.replace(':reportId', encodeURIComponent(reportId)),
    );
  }

  public deleteTenantReport(tenantId: string, reportId: string): Observable<void> {
    return this.httpClient.delete<void>(
      RESTServerRoute.REST_DELETE_TENANT_REPORT.replace(':tenantId', encodeURIComponent(tenantId)).replace(
        ':reportId',
        encodeURIComponent(reportId),
      ),
    );
  }

  private safeGetTimezone(): string {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
      return 'UTC';
    }

    try {
      return Intl.DateTimeFormat().resolvedOptions()?.timeZone ?? 'UTC';
    } catch {
      return 'UTC';
    }
  }
}
